import { forwardRef } from "react"
import {
  Image as BaseImage,
  getOptimizedMediaUrl as baseGetOptimizedMediaUrl,
  getSrcSet as baseGetSrcSet,
  registerImageCdnDomain,
  getImageCdnDomain,
  FACTORS,
  type ImageProps,
  type FitOptions,
} from "@decocms/blocks/hooks"

// `data.decoassets.com` is an origin alias of `assets.decocache.com` (same path
// layout). The upstream Image component only strips `assets.decocache.com/`
// before forwarding to `decoims.com/image?src=…`, so URLs from
// `data.decoassets.com` reach the CDN unstripped and come back as 403.
//
// Some legacy CMS blocks also persisted `decoims.com/<site>/...` (path-only,
// no `/image?src=` query) — feeding that to the framework produces a recursive
// `decoims.com/image?src=https://decoims.com/...` URL that returns 403.
// Treat both as equivalent decocache origins so a single `getOptimizedMediaUrl`
// pass produces the correct CDN URL.
//
// Normalizing here keeps every call site (Picture/CustomImage/etc) covered.
const DATA_DECOASSETS = "https://data.decoassets.com/"
const DECOIMS_RAW = "https://decoims.com/"
const DECOIMS_IMAGE = "https://decoims.com/image"
const ASSETS_DECOCACHE = "https://assets.decocache.com/"

const normalizeSrc = (src: string) => {
  if (src.startsWith(DATA_DECOASSETS)) {
    return ASSETS_DECOCACHE + src.slice(DATA_DECOASSETS.length)
  }
  // Path-only `decoims.com/<site>/...` (no query) — admin saved a malformed
  // URL pointing at the CDN. Rewrite to the source so framework can re-optimize.
  if (src.startsWith(DECOIMS_RAW) && !src.startsWith(DECOIMS_IMAGE)) {
    return ASSETS_DECOCACHE + src.slice(DECOIMS_RAW.length)
  }
  return src
}

export const getOptimizedMediaUrl: typeof baseGetOptimizedMediaUrl = (opts) =>
  baseGetOptimizedMediaUrl({ ...opts, originalSrc: normalizeSrc(opts.originalSrc) })

export const getSrcSet: typeof baseGetSrcSet = (originalSrc, ...rest) =>
  baseGetSrcSet(normalizeSrc(originalSrc), ...rest)

export const Image = forwardRef<HTMLImageElement, ImageProps>(function Image(props, ref) {
  // Legacy parity: the Fresh Image emitted no `sizes` (spec fallback: 100vw),
  // so browsers picked the largest srcset candidate. BaseImage instead
  // defaults missing `sizes` to "(max-width: 768px) 100vw, 50vw", halving the
  // resolution of full-width images on desktop (DECO-5585). Pin 100vw unless
  // the call site passes its own.
  return <BaseImage {...props} src={normalizeSrc(props.src)} sizes={props.sizes ?? "100vw"} ref={ref} />
})

export default Image

export { registerImageCdnDomain, getImageCdnDomain, FACTORS }
export type { ImageProps, FitOptions }
